import { MenuManagementItemActionTypes } from './constants';

type State = {
    items: any[]; // Replace `any` with the appropriate type for items
    error: string | null;
    loading: boolean;
    message: string | null;
};

const initialState: State = {
    items: [],
    error: null,
    loading: false,
    message: null,
};

/* =======================
   Common Types
======================= */

type LanguageValue = {
    english?: string;
    hindi?: string;
    gujarati?: string;
};

/* =======================
   Variant Type
======================= */

interface ItemVariantPayload {
    variant_name: LanguageValue;
    price: number;
    // description?: string;
}

/* =======================
   Item Type
======================= */

interface ItemPayload {
    category_id: string;
    item_name: LanguageValue;
    online_display_name?: string;
    price: number;
    description?: string;
    dietary: 'veg' | 'non_veg';
    available_order_type: string[];
    gst_type: 'services' | 'goods' | 'none';
    quantity_type?: 'weight' | 'piece' | 'volume' | 'none';
    quantity_params?: 'gm' | 'kg' | 'ml' | 'l' | 'none';
    quantity_value?: string | 'none';
    kot_id?: string;
    variants?: ItemVariantPayload[];
}

/* =======================
   Root Payload
======================= */

export interface ItemRegBulk {
    business_id: string;
    items: ItemPayload[];
}

interface BulkItemUpdate {
    item_id: string;
    category_id: string;
    item_name: LanguageValue;
    online_display_name?: string;
    price: number;
    description?: string;
    dietary: 'veg' | 'non_veg';
    available_order_type: string[];
    gst_type: 'services' | 'goods' | 'none';
    quantity_type?: 'weight' | 'piece' | 'volume' | 'none';
    quantity_params?: 'gm' | 'kg' | 'ml' | 'l' | 'none';
    quantity_value?: string | 'none';
    kot_id?: string;
    variants?: UpdateVariantPayload[];
}

interface UpdateVariantPayload {
    action: 'update' | 'delete' | 'insert';
    variant_id?: string;
    price: string;
    variant_name: LanguageValue;
}

type MenuManagementItemActions =
    | { type: typeof MenuManagementItemActionTypes.UPDATE_OUTLET_PRICE }
    | {
          type: typeof MenuManagementItemActionTypes.UPDATE_OUTLET_PRICE_SUCCESS;
          payload: {
              outlet_id: string;
              item_id: string;
              price: number;
          };
      }
    | { type: typeof MenuManagementItemActionTypes.UPDATE_OUTLET_PRICE_ERROR; payload: { error: string } }
    | {
          type: typeof MenuManagementItemActionTypes.REGISTER_ITEM;
          payload: FormData;
      }
    | { type: typeof MenuManagementItemActionTypes.REGISTER_ITEM_SUCCESS; payload: { message: string } }
    | { type: typeof MenuManagementItemActionTypes.REGISTER_ITEM_ERROR; payload: { error: string } }
    | {
          type: typeof MenuManagementItemActionTypes.REGISTER_ITEM_BULK;
          payload: ItemRegBulk;
      }
    | { type: typeof MenuManagementItemActionTypes.REGISTER_ITEM_BULK_SUCCESS; payload: { message: string } }
    | { type: typeof MenuManagementItemActionTypes.REGISTER_ITEM_BULK_ERROR; payload: { error: string } }
    | { type: typeof MenuManagementItemActionTypes.UPDATE_ITEM; payload: FormData }
    | { type: typeof MenuManagementItemActionTypes.UPDATE_ITEM_SUCCESS; payload: { message: string } }
    | { type: typeof MenuManagementItemActionTypes.UPDATE_ITEM_ERROR; payload: { error: string } }
    | {
          type: typeof MenuManagementItemActionTypes.DELETE_ITEM;
          payload: {
              item_id: string;
          };
      }
    | { type: typeof MenuManagementItemActionTypes.DELETE_ITEM_SUCCESS; payload: { message: string } }
    | { type: typeof MenuManagementItemActionTypes.DELETE_ITEM_ERROR; payload: { error: string } }
    | {
          type: typeof MenuManagementItemActionTypes.ITEM_UPDATE_ISACTIVE;
          payload: {
              item_id: string;
              is_active: boolean;
          };
      }
    | { type: typeof MenuManagementItemActionTypes.ITEM_UPDATE_ISACTIVE_SUCCESS; payload: { message: string } }
    | { type: typeof MenuManagementItemActionTypes.ITEM_UPDATE_ISACTIVE_ERROR; payload: { error: string } }
    | {
          type: typeof MenuManagementItemActionTypes.ITEM_UPDATE_DISABLE;
          payload: {
              item_id: string;
              outlet_id: string;
              disable_until: string;
              is_active: boolean;
          };
      }
    | { type: typeof MenuManagementItemActionTypes.ITEM_UPDATE_DISABLE_SUCCESS; payload: { message: string } }
    | { type: typeof MenuManagementItemActionTypes.ITEM_UPDATE_DISABLE_ERROR; payload: { error: string } };

const MenuManagementItemReducer = (state = initialState, action: any): State => {
    switch (action.type) {
        /* =======================
           BULK ITEM REGISTER
        ======================= */

        case MenuManagementItemActionTypes.REGISTER_ITEM_BULK:
            return {
                ...state,
                loading: true,
                error: null,
                message: null,
            };

        case MenuManagementItemActionTypes.REGISTER_ITEM_BULK_SUCCESS:
            return {
                ...state,
                loading: false,
                message: action.payload.message,
            };

        case MenuManagementItemActionTypes.REGISTER_ITEM_BULK_ERROR:
            return {
                ...state,
                loading: false,
                error: action.payload.error,
            };

        case MenuManagementItemActionTypes.UPDATE_ITEM_BULK:
            return {
                ...state,
                loading: true,
                error: null,
                message: null,
            };

        case MenuManagementItemActionTypes.UPDATE_ITEM_BULK_SUCCESS:
            return {
                ...state,
                loading: false,
                message: action.payload.message,
            };

        case MenuManagementItemActionTypes.UPDATE_ITEM_BULK_ERROR:
            return {
                ...state,
                loading: false,
                error: action.payload.error,
            };

        /* =======================
           SINGLE ITEM REGISTER
        ======================= */

        case MenuManagementItemActionTypes.REGISTER_ITEM:
            return {
                ...state,
                loading: true,
                error: null,
            };

        case MenuManagementItemActionTypes.REGISTER_ITEM_SUCCESS:
            return {
                ...state,
                loading: false,
                message: action.payload.message,
            };

        case MenuManagementItemActionTypes.REGISTER_ITEM_ERROR:
            return {
                ...state,
                loading: false,
                error: action.payload.error,
            };

        /* =======================
           UPDATE ITEM
        ======================= */

        case MenuManagementItemActionTypes.UPDATE_ITEM:
            return {
                ...state,
                loading: true,
                error: null,
            };

        case MenuManagementItemActionTypes.UPDATE_ITEM_SUCCESS:
            return {
                ...state,
                loading: false,
                message: action.payload.message,
            };

        case MenuManagementItemActionTypes.UPDATE_ITEM_ERROR:
            return {
                ...state,
                loading: false,
                error: action.payload.error,
            };

        /* =======================
           DELETE ITEM
        ======================= */

        case MenuManagementItemActionTypes.DELETE_ITEM:
            return {
                ...state,
                loading: true,
                error: null,
            };

        case MenuManagementItemActionTypes.DELETE_ITEM_SUCCESS:
            return {
                ...state,
                loading: false,
                items: state.items.filter((item) => item.item_id !== action.payload.item_id),
                message: action.payload.message,
            };

        case MenuManagementItemActionTypes.DELETE_ITEM_ERROR:
            return {
                ...state,
                loading: false,
                error: action.payload.error,
            };

        /* =======================
           TOGGLE ITEM ACTIVE
        ======================= */

        case MenuManagementItemActionTypes.ITEM_UPDATE_ISACTIVE:
            return {
                ...state,
                loading: true,
                error: null,
            };

        case MenuManagementItemActionTypes.ITEM_UPDATE_ISACTIVE_SUCCESS:
            return {
                ...state,
                loading: false,
                items: state.items.map((item) =>
                    item.item_id === action.payload.item_id ? { ...item, is_active: action.payload.is_active } : item
                ),
                message: action.payload.message,
            };

        case MenuManagementItemActionTypes.ITEM_UPDATE_ISACTIVE_ERROR:
            return {
                ...state,
                loading: false,
                error: action.payload.error,
            };

        /* =======================
           DISABLE ITEM (OUTLET)
        ======================= */

        case MenuManagementItemActionTypes.ITEM_UPDATE_DISABLE:
            return {
                ...state,
                loading: true,
                error: null,
            };

        case MenuManagementItemActionTypes.ITEM_UPDATE_DISABLE_SUCCESS:
            return {
                ...state,
                loading: false,
                message: action.payload.message,
            };

        case MenuManagementItemActionTypes.ITEM_UPDATE_DISABLE_ERROR:
            return {
                ...state,
                loading: false,
                error: action.payload.error,
            };

        /* =======================
           UPDATE OUTLET PRICE
        ======================= */

        case MenuManagementItemActionTypes.UPDATE_OUTLET_PRICE:
            return {
                ...state,
                loading: true,
                error: null,
            };

        case MenuManagementItemActionTypes.UPDATE_OUTLET_PRICE_SUCCESS:
            return {
                ...state,
                loading: false,
                items: state.items.map((item) =>
                    item.item_id === action.payload.item_id
                        ? {
                              ...item,
                              outlet_prices: item.outlet_prices?.map((price: any) =>
                                  price.outlet_id === action.payload.outlet_id
                                      ? {
                                            ...price,
                                            price: action.payload.price,
                                        }
                                      : price
                              ),
                          }
                        : item
                ),
            };

        case MenuManagementItemActionTypes.UPDATE_OUTLET_PRICE_ERROR:
            return {
                ...state,
                loading: false,
                error: action.payload.error,
            };

        case MenuManagementItemActionTypes.CLEAR_MENU_ITEM_MESSAGE:
            return {
                ...state,
                message: null,
            };

        default:
            return state;
    }
};

export default MenuManagementItemReducer;
